import {
    Check,
    SearchIcon,
    XIcon,
} from "lucide-react";
import { Button } from "@component/button";
import { Input } from "./input";
import { SearchCandidateFilter } from "@type/candidate";
import { Label } from "./label";
import { useState } from "react";
import { useUserProfileContext } from "@context/user_profile";

interface FilterModalProps {
    filter: SearchCandidateFilter;
    onClose: () => void;
    onReset: () => void;
    onChange: (key: keyof SearchCandidateFilter, value: string | string[] | number | boolean) => void;
    onFilter: (filter: SearchCandidateFilter) => void;
}

export function FilterModal({ filter, onClose, onReset, onChange, onFilter }: FilterModalProps) {
    const [, currentUserInfo] = useUserProfileContext();
    const [selectedCandidateTypes, setSelectedCandidateTypes] = useState<string[]>(filter.candidate_type || []);
    // Location preferences options

    const handleCandidateTypeToggle = (type: string) => {
        let updatedTypes = [...selectedCandidateTypes];
        if (updatedTypes.includes(type)) {
            updatedTypes = updatedTypes.filter((t) => t !== type);
        } else {
            updatedTypes.push(type);
        }
        setSelectedCandidateTypes(updatedTypes);
        onChange("candidate_type", updatedTypes);
    }

    const handleShortlistedToggle = () => {
        const isShortlisted = filter.shortlisted || false;
        onChange("shortlisted", !isShortlisted);
    }

    const handleCompletedInterviewToggle = () => {
        const isCompleted = filter.completed_interview || false;
        onChange("completed_interview", !isCompleted);
    }

    return (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
            <div className="bg-white dark:bg-gray-900 rounded-lg shadow-lg w-full max-w-5xl max-h-[90vh] overflow-y-auto relative">
                <button
                    onClick={onClose}
                    className="absolute top-2 right-2 p-2 rounded-full bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 transition-colors"
                    aria-label="Close"
                >
                    <XIcon className="h-5 w-5" />
                </button>

                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center">
                            <SearchIcon className="h-5 w-5 text-blue-600 mr-2" />

                            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
                                Filter your candidates
                            </h2>
                        </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Skills or Keywords
                                </label>
                                <Input
                                    type="text"
                                    placeholder="Python, Kubernetes, Django"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-gray-900 dark:text-gray-100 dark:bg-gray-800"
                                    value={filter.skills}
                                    onChange={(e) => onChange("skills", e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Companies
                                </label>
                                <Input
                                    type="text"
                                    placeholder="Google, Amazon, Microsoft"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-gray-900 dark:text-gray-100 dark:bg-gray-800"
                                    value={filter.company}
                                    onChange={(e) => onChange("company", e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Titles
                                </label>
                                <Input
                                    type="text"
                                    placeholder="Software Engineer, Lawyer, Consultant"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-gray-900 dark:text-gray-100 dark:bg-gray-800"
                                    value={filter.title}
                                    onChange={(e) => onChange("title", e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Country
                                </label>
                                <Input
                                    type="text"
                                    placeholder="United States, Latin America, Asia"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-gray-900 dark:text-gray-100 dark:bg-gray-800"
                                    value={filter.country}
                                    onChange={(e) => onChange("country", e.target.value)}
                                />
                            </div>


                            {["admin", "super_admin", "hr"].includes(currentUserInfo?.role || "") && (
                                <div className="space-y-2">
                                    <Label className="text-sm font-medium">Candidate Type</Label>
                                    {["admin", "super_admin"].includes(currentUserInfo?.role || "") ? (
                                        <div className="flex flex-row gap-6 pt-1">
                                            <div
                                                className="flex items-center space-x-2 cursor-pointer"
                                                onClick={() => handleCandidateTypeToggle("byoc")}
                                            >
                                                <div
                                                    className={`w-4 h-4 border rounded flex items-center justify-center cursor-pointer transition-colors ${(filter.candidate_type || []).includes("byoc")
                                                        ? "bg-blue-600 border-blue-600"
                                                        : "border-gray-300 hover:border-gray-400"
                                                        }`}
                                                >
                                                    {(filter.candidate_type || []).includes("byoc") && (
                                                        <Check className="w-3 h-3 text-white" />
                                                    )}
                                                </div>

                                                <label
                                                    htmlFor="byoc-checkbox"
                                                    className={`text-sm cursor-pointer leading-none ${(filter.candidate_type || []).includes("byoc")
                                                        ? "font-medium text-gray-900"
                                                        : "font-normal text-gray-700"
                                                        }`}
                                                >
                                                    BYOC
                                                </label>
                                            </div>
                                            <div
                                                className="flex items-center space-x-2 cursor-pointer"
                                                onClick={() => handleCandidateTypeToggle("talent-pool")}
                                            >
                                                <div
                                                    className={`w-4 h-4 border rounded flex items-center justify-center cursor-pointer transition-colors ${(filter.candidate_type || []).includes("talent-pool")
                                                        ? "bg-blue-600 border-blue-600"
                                                        : "border-gray-300 hover:border-gray-400"
                                                        }`}
                                                >
                                                    {(filter.candidate_type || []).includes("talent-pool") && (
                                                        <Check className="w-3 h-3 text-white" />
                                                    )}
                                                </div>

                                                <label
                                                    htmlFor="talent-pool-checkbox"
                                                    className={`text-sm cursor-pointer leading-none ${(filter.candidate_type || []).includes("talent-pool")
                                                        ? "font-medium text-gray-900"
                                                        : "font-normal text-gray-700"
                                                        }`}
                                                >
                                                    Talent Pool
                                                </label>
                                            </div>
                                            <div
                                                className="flex items-center space-x-2 cursor-pointer"
                                                onClick={handleCompletedInterviewToggle}
                                            >
                                                <div
                                                    className={`w-4 h-4 border rounded flex items-center justify-center cursor-pointer transition-colors ${filter.completed_interview
                                                        ? "bg-blue-600 border-blue-600"
                                                        : "border-gray-300 hover:border-gray-400"
                                                        }`}
                                                >
                                                    {filter.completed_interview && (
                                                        <Check className="w-3 h-3 text-white" />
                                                    )}
                                                </div>

                                                <label
                                                    htmlFor="completed-interview-checkbox"
                                                    className={`text-sm cursor-pointer leading-none ${filter.completed_interview
                                                        ? "font-medium text-gray-900"
                                                        : "font-normal text-gray-700"
                                                        }`}
                                                >
                                                    Completed Interview
                                                </label>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="flex flex-row gap-6 pt-1">
                                            <div
                                                className="flex items-center space-x-2 cursor-pointer"
                                                onClick={handleShortlistedToggle}
                                            >
                                                <div
                                                    className={`w-4 h-4 border rounded flex items-center justify-center cursor-pointer transition-colors ${filter.shortlisted
                                                        ? "bg-blue-600 border-blue-600"
                                                        : "border-gray-300 hover:border-gray-400"
                                                        }`}
                                                >
                                                    {filter.shortlisted && (
                                                        <Check className="w-3 h-3 text-white" />
                                                    )}
                                                </div>

                                                <label
                                                    htmlFor="shortlisted-checkbox"
                                                    className={`text-sm cursor-pointer leading-none ${filter.shortlisted
                                                        ? "font-medium text-gray-900"
                                                        : "font-normal text-gray-700"
                                                        }`}
                                                >
                                                        Marked as Favourite 
                                                </label>
                                            </div>
                                                <div
                                                    className="flex items-center space-x-2 cursor-pointer"
                                                    onClick={handleCompletedInterviewToggle}
                                                >
                                                    <div
                                                        className={`w-4 h-4 border rounded flex items-center justify-center cursor-pointer transition-colors ${filter.completed_interview
                                                            ? "bg-blue-600 border-blue-600"
                                                            : "border-gray-300 hover:border-gray-400"
                                                            }`}
                                                    >
                                                        {filter.completed_interview && (
                                                            <Check className="w-3 h-3 text-white" />
                                                        )}
                                                    </div>

                                                    <label
                                                        htmlFor="completed-interview-checkbox"
                                                        className={`text-sm cursor-pointer leading-none ${filter.completed_interview
                                                            ? "font-medium text-gray-900"
                                                            : "font-normal text-gray-700"
                                                            }`}
                                                    >
                                                        Completed Interview
                                                    </label>
                                                </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        <div className="space-y-6">

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Degree Requirements
                                </label>
                                <Input
                                    type="text"
                                    placeholder="Bachelors, Masters, Doctorate"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-gray-900 dark:text-gray-100 dark:bg-gray-800"
                                    value={filter.degree}
                                    onChange={(e) => onChange("degree", e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Universities
                                </label>
                                <Input
                                    type="text"
                                    placeholder="Harvard, IIIT, Top 50 program"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-gray-900 dark:text-gray-100 dark:bg-gray-800"
                                    value={filter.university}
                                    onChange={(e) => onChange("university", e.target.value)}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Field of Study
                                </label>
                                <Input
                                    type="text"
                                    placeholder="Computer Science, Chemistry, Literature"
                                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-gray-900 dark:text-gray-100 dark:bg-gray-800"
                                    value={filter.field_of_study}
                                    onChange={(e) => onChange("field_of_study", e.target.value)}
                                />
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Min Experience
                                    </label>
                                    <Input
                                        type="number"
                                        placeholder="0"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-gray-900 dark:text-gray-100 dark:bg-gray-800"
                                        value={filter.min_experience}
                                        onChange={(e) => onChange("min_experience", e.target.value)}
                                        min="0"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Max Experience
                                    </label>
                                    <Input
                                        type="number"
                                        placeholder="40"
                                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-md text-gray-900 dark:text-gray-100 dark:bg-gray-800"
                                        value={filter.max_experience}
                                        onChange={(e) => onChange("max_experience", e.target.value)}
                                        min="0"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="flex justify-end space-x-3 mt-6">
                        <Button
                            variant="outline"
                            className="text-blue-600 border-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950"
                            onClick={() => {
                                onReset();
                            }}
                        >
                            Clear filters
                        </Button>
                        <Button
                            className="bg-blue-600 hover:bg-blue-700 text-white"
                            onClick={() => {
                                onFilter(filter);
                                onClose();
                            }}
                        >
                            Save filters
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    );
}
